import React, { useEffect, useState, useCallback } from 'react';
import {
  Layers, TrendingUp, Flame, Thermometer, Snowflake,
  ChevronRight, Mail, Phone, Building, Calendar, Star,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { getLeads, updateLead } from '@/services/api';

const PRIORITY_CONFIG = {
  hot:  { label: 'Hot',  classes: 'bg-red-500/20 text-red-400 border-red-500/30',    Icon: Flame },
  warm: { label: 'Warm', classes: 'bg-orange-500/20 text-orange-400 border-orange-500/30', Icon: Thermometer },
  cold: { label: 'Cold', classes: 'bg-blue-500/20 text-blue-400 border-blue-500/30',   Icon: Snowflake },
};

function PriorityBadge({ priority }) {
  const cfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.cold;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.classes}`}>
      <cfg.Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function QualityScore({ score }) {
  if (!score) return null;
  const color = score >= 8 ? 'text-green-400' : score >= 5 ? 'text-yellow-400' : 'text-gray-400';
  return (
    <span className={`flex items-center gap-1 text-sm font-bold ${color}`}>
      <Star className="w-3.5 h-3.5" />
      {score}/10
    </span>
  );
}

function LeadCard({ lead, onClick }) {
  return (
    <Card
      className="bg-white/5 border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 cursor-pointer group"
      onClick={() => onClick(lead)}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PriorityBadge priority={lead.priority} />
            <QualityScore score={lead.quality_score} />
          </div>
          <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-300 transition-colors" />
        </div>
        <h3 className="text-white font-semibold mb-0.5">{lead.name}</h3>
        <p className="text-sm text-gray-400 mb-0.5">{lead.email}</p>
        {lead.company && <p className="text-sm text-cyan-400 mb-2">{lead.company}</p>}
        {lead.use_case && (
          <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{lead.use_case}</p>
        )}
        <p className="text-xs text-gray-600 mt-3">
          {new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      </CardContent>
    </Card>
  );
}

function TranscriptViewer({ transcript }) {
  if (!transcript || transcript.length === 0) {
    return <p className="text-gray-500 text-sm">No transcript available.</p>;
  }
  return (
    <div className="space-y-3">
      {transcript.map((msg, i) => (
        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
            msg.role === 'user'
              ? 'bg-blue-600/30 text-blue-100 rounded-br-sm'
              : 'bg-white/5 text-gray-200 border border-white/10 rounded-bl-sm'
          }`}>
            {msg.parts?.[0]?.text}
          </div>
        </div>
      ))}
    </div>
  );
}

function LeadDetailSheet({ lead, open, onClose, onUpdate }) {
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('cold');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setNotes(lead.notes || '');
      setPriority(lead.priority || 'cold');
    }
  }, [lead]);

  const handleSave = async () => {
    if (!lead) return;
    setSaving(true);
    try {
      const updated = await updateLead(lead.id, { priority, notes });
      onUpdate(updated);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="bg-[#0f0f10] border-white/10 text-white w-[480px] max-w-full p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b border-white/10 flex-shrink-0">
          <SheetTitle className="text-white text-lg">{lead.name}</SheetTitle>
          <div className="flex items-center gap-2 mt-1">
            <PriorityBadge priority={priority} />
            <QualityScore score={lead.quality_score} />
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-5 space-y-6 pb-10">

            {/* Contact Info */}
            <section>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Contact</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <a href={`mailto:${lead.email}`} className="text-blue-400 hover:text-blue-300">{lead.email}</a>
                </div>
                {lead.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-300">{lead.phone}</span>
                  </div>
                )}
                {lead.company && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-300">{lead.company}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-400">
                    {new Date(lead.created_at).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </section>

            {/* Requirements */}
            {(lead.use_case || lead.query) && (
              <section>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Requirements</h4>
                {lead.use_case && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Use Case</p>
                    <p className="text-sm text-gray-300">{lead.use_case}</p>
                  </div>
                )}
                {lead.query && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Specific Query</p>
                    <p className="text-sm text-gray-300">{lead.query}</p>
                  </div>
                )}
              </section>
            )}

            {/* AI Intelligence */}
            {(lead.summary || lead.key_pain_points?.length > 0 || lead.next_steps?.length > 0) && (
              <section>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">AI Intelligence</h4>
                <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-4">
                  {lead.summary && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Summary</p>
                      <p className="text-sm text-gray-300 leading-relaxed">{lead.summary}</p>
                    </div>
                  )}
                  {lead.key_pain_points?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Key Pain Points</p>
                      <ul className="space-y-1">
                        {lead.key_pain_points.map((pt, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <span className="text-red-400 mt-0.5 flex-shrink-0">•</span>
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {lead.next_steps?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Suggested Next Steps</p>
                      <ul className="space-y-1">
                        {lead.next_steps.map((step, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <span className="text-cyan-400 mt-0.5 flex-shrink-0">{i + 1}.</span>
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Priority override */}
            <section>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Priority</h4>
              <div className="flex gap-2">
                {['hot', 'warm', 'cold'].map(p => (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      priority === p
                        ? 'bg-white/10 border-white/30 text-white'
                        : 'bg-transparent border-white/10 text-gray-500 hover:text-gray-300 hover:border-white/20'
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </section>

            {/* Notes */}
            <section>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Notes</h4>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add internal notes…"
                className="bg-white/5 border-white/10 text-gray-300 placeholder-gray-600 focus:border-blue-500/50 resize-none"
                rows={3}
              />
              <Button
                onClick={handleSave}
                disabled={saving}
                size="sm"
                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </section>

            {/* Transcript */}
            <section>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Transcript</h4>
              <div className="bg-white/3 rounded-xl p-4 border border-white/5">
                <TranscriptViewer transcript={lead.transcript} />
              </div>
            </section>

          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default function Dashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    getLeads()
      .then(setLeads)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleLeadClick = useCallback((lead) => {
    setSelectedLead(lead);
    setSheetOpen(true);
  }, []);

  const handleLeadUpdate = useCallback((updated) => {
    setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
    setSelectedLead(updated);
  }, []);

  const filteredLeads = activeTab === 'all'
    ? leads
    : leads.filter(l => l.priority === activeTab);

  const stats = {
    total: leads.length,
    hot:   leads.filter(l => l.priority === 'hot').length,
    warm:  leads.filter(l => l.priority === 'warm').length,
    cold:  leads.filter(l => l.priority === 'cold').length,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0f0f10]">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">INVARYA</span>
              <p className="text-xs text-gray-500">Sales Dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-8">
            {[
              { label: 'Total', value: stats.total, color: 'text-white' },
              { label: 'Hot',   value: stats.hot,   color: 'text-red-400' },
              { label: 'Warm',  value: stats.warm,  color: 'text-orange-400' },
              { label: 'Cold',  value: stats.cold,  color: 'text-blue-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Inbound Leads</h1>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white/5 border border-white/10">
              {['all', 'hot', 'warm', 'cold'].map(tab => (
                <TabsTrigger
                  key={tab}
                  value={tab}
                  className="data-[state=active]:bg-white/10 text-gray-400 data-[state=active]:text-white capitalize"
                >
                  {tab}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-28">
            <TrendingUp className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No leads yet</p>
            <p className="text-gray-600 text-sm mt-1">
              Leads captured via the chat widget will appear here.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLeads.map(lead => (
              <LeadCard key={lead.id} lead={lead} onClick={handleLeadClick} />
            ))}
          </div>
        )}
      </main>

      <LeadDetailSheet
        lead={selectedLead}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onUpdate={handleLeadUpdate}
      />
    </div>
  );
}
